import {
  BaseQueryApi,
  QueryReturnValue,
} from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import {
  CreateSavedSearchApiArg,
  CreateSavedSearchApiResponse,
  DeleteSavedSearchApiArg,
  DeleteSavedSearchApiResponse,
  ReadSavedSearchApiArg,
  ReadSavedSearchApiResponse,
  ReadSavedSearchesApiArg,
  ReadSavedSearchesApiResponse,
  ReadSearchApiArg,
  ReadSearchApiResponse,
  ReadSearchEnrichedFiltersApiArg,
  ReadSearchEnrichedFiltersApiResponse,
  ReadSelectiveSearchApiArg,
  ReadSelectiveSearchApiResponse,
  SearchScope,
  UpdateSavedSearchApiArg,
  UpdateSavedSearchApiResponse,
} from 'lib-search-types';
import {
  EndpointExtraOptions,
  EndpointOptions,
  QueryError,
} from 'lib-shared-auth';
import { DeepWritable } from 'ts-essentials';

import { api } from 'app/app.api';

import { transformReadSearchArg } from './utils/transformArg';
import {
  transformCreateSavedSearchResponse,
  transformReadSavedSearchesResponse,
  transformReadSavedSearchResponse,
  transformReadSearchResponse,
  transformUpdateSavedSearchResponse,
} from './utils/transformResponse';

export const searchApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ============================================================================
    // Generic loosely typed search endpoints
    // ============================================================================
    readSearch: builder.query(getReadSearchEndpointDefinition()),
    readSearchEnriched: builder.query(
      getReadSearchEndpointDefinition(undefined, true),
    ),

    // ============================================================================
    // Scope-specific strictly typed search endpoints
    // ============================================================================
    readSearchCompanies: builder.query(
      getReadSearchEndpointDefinition('companies'),
    ),
    readSearchConversations: builder.query(
      getReadSearchEndpointDefinition('conversations'),
    ),
    readSearchQueues: builder.query(getReadSearchEndpointDefinition('queues')),
    readSearchTags: builder.query(getReadSearchEndpointDefinition('tags')),
    readSearchTeams: builder.query(getReadSearchEndpointDefinition('teams')),
    readSearchUsers: builder.query(getReadSearchEndpointDefinition('users')),

    // ============================================================================
    // Enriched search endpoints
    // ============================================================================
    readSearchConversationsEnriched: builder.query(
      getReadSearchEndpointDefinition('conversations', true),
    ),
    readSearchQueuesEnriched: builder.query(
      getReadSearchEndpointDefinition('queues', true),
    ),
    readSearchTeamsEnriched: builder.query(
      getReadSearchEndpointDefinition('teams', true),
    ),
    readSearchUsersEnriched: builder.query(
      getReadSearchEndpointDefinition('users', true),
    ),

    // ============================================================================
    // Saved search endpoints
    // ============================================================================
    readSavedSearches: builder.query<
      ReadSavedSearchesApiResponse,
      ReadSavedSearchesApiArg
    >({
      query: () => ({
        version: 4,
        path: 'search/saved-searches/search',
        method: 'POST',
      }),
      transformResponse: transformReadSavedSearchesResponse,
    }),
    readSavedSearch: builder.query<
      ReadSavedSearchApiResponse,
      ReadSavedSearchApiArg
    >({
      query: ({ id }) => ({
        version: 4,
        path: `search/saved-searches/${id}`,
      }),
      transformResponse: transformReadSavedSearchResponse,
    }),
    createSavedSearch: builder.mutation<
      CreateSavedSearchApiResponse,
      CreateSavedSearchApiArg
    >({
      query: (body) => ({
        version: 4,
        path: 'search/saved-searches',
        method: 'POST',
        body: {
          scope: body.searchRequest.scope,
          ...body,
        },
      }),
      transformResponse: transformCreateSavedSearchResponse,
    }),
    updateSavedSearch: builder.mutation<
      UpdateSavedSearchApiResponse,
      UpdateSavedSearchApiArg
    >({
      query: ({ id, ...body }) => ({
        version: 4,
        path: `search/saved-searches/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: transformUpdateSavedSearchResponse,
    }),
    deleteSavedSearch: builder.mutation<
      DeleteSavedSearchApiResponse,
      DeleteSavedSearchApiArg
    >({
      query: ({ id }) => ({
        version: 4,
        path: `search/saved-searches/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchReadSavedSearchListQuery = dispatch(
          searchApi.util.updateQueryData(
            'readSavedSearches',
            undefined,
            (draft) => {
              const mutableSavedSearches = draft.data
                .savedSearches as DeepWritable<typeof draft.data.savedSearches>;

              const index = draft.data.savedSearches.findIndex(
                (savedSearch) => savedSearch.id === id,
              );

              if (index !== -1) {
                mutableSavedSearches.splice(index, 1);
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadSavedSearchListQuery.undo();
        }
      },
    }),

    // ============================================================================
    // Selective search endpoint returning only specific fields of the search results.
    // ============================================================================
    readSelectiveSearch: builder.query<
      ReadSelectiveSearchApiResponse,
      ReadSelectiveSearchApiArg
    >({
      query: (arg) => ({
        version: 4,
        path: `search/selective/${arg.scope}`,
        method: 'POST',
        body: {
          ...transformReadSearchArg(arg),
          scope: undefined,
        },
      }),
    }),

    // ============================================================================
    // Other search-related endpoints
    // ============================================================================
    readSearchEnrichedFilters: builder.query<
      ReadSearchEnrichedFiltersApiResponse,
      ReadSearchEnrichedFiltersApiArg
    >({
      query: (arg) => {
        return {
          version: 4,
          path: 'search/enrich',
          method: 'POST',
          body: {
            scope: arg.scope,
            searchRequest: transformReadSearchArg(arg),
          },
        };
      },
    }),

    // ============================================================================
    // Search view specific endpoint. This should only be used in the search view which has specific
    // needs in terms of request/response shape and would require too much refactoring to be
    // accommodated by the generic endpoints above.
    // ============================================================================
    readAdvancedSearchResults: builder.query<
      ReadSearchApiResponse<'conversations' | 'users' | 'companies', true>,
      ReadSearchApiArg<'conversations' | 'users' | 'companies', true>
    >({
      query: (arg) => ({
        version: 4,
        path: `search/${arg.scope}`,
        method: 'POST',
        body: {
          ...transformReadSearchArg(arg),
          scope: undefined,
          // Pagination is 1-based in the frontend and 0-based in the backend
          page: (arg.page ?? 1) - 1,
        },
      }),
      transformResponse: (response: any, arg: any) => {
        // Pagination is 1-based in the frontend and 0-based in the backend
        response.meta.page = response.meta?.page + 1;
        return transformReadSearchResponse(response, arg);
      },
    }),
  }),
});

/**
 * Returns the endpoint definition for reading search results.
 *
 * @param scope - The search scope for which to create the endpoint definition.
 *   If not provided, the scope will be inferred from the request body.
 * @param enriched - Whether to return enriched resources with expanded
 *   relations.
 */
function getReadSearchEndpointDefinition<
  TScope extends SearchScope | undefined = undefined,
  TEnriched extends boolean = false,
>(scope?: TScope, _enriched?: TEnriched) {
  return {
    async queryFn(
      _arg: [TScope] extends [SearchScope]
        ? Omit<ReadSearchApiArg<TScope, TEnriched>, 'scope'> & {
            scope?: TScope;
          }
        : ReadSearchApiArg<SearchScope, TEnriched>,
      _api: BaseQueryApi,
      _extraOptions: EndpointExtraOptions,
      baseQuery: (
        arg: EndpointOptions | string,
      ) => MaybePromise<
        QueryReturnValue<unknown, QueryError, Record<string, unknown>>
      >,
    ) {
      const arg = transformReadSearchArg(_arg as ReadSearchApiArg);

      const response = await baseQuery({
        version: 4,
        path: `search/${scope ?? arg.scope}`,
        method: 'POST',
        body: {
          ...arg,
          scope: undefined,
        },
      });

      if (response.error) {
        return { error: response.error };
      }

      type Scope = [TScope] extends [SearchScope] ? TScope : SearchScope;

      return {
        data: transformReadSearchResponse<Scope, TEnriched>(
          response.data as ReadSearchApiResponse<Scope, TEnriched>,
          (scope ?? arg.scope) as Scope,
        ),
      };
    },
  };
}
